import React, { useCallback, useEffect, useMemo, useState } from "react";
import ApexCharts from "apexcharts";
import Chart from "react-apexcharts";
import { useApi } from "utils/hooks/use-api";
import { bucketService } from "api/services/bucket-service";
import { ChartFormatters } from "../../../helper/chart-formatters";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import styles from "./usage-chart.module.scss";

export interface IChartProps {
  id: string;
}

function UsageChart(props: IChartProps) {
  const [api, isLoading] = useApi();
  const [range, setRange] = useState<string>("1D");
  const [requestFailed, setRequestFailed] = useState(true);

  const from = useMemo(() => ChartFormatters.TsRange(range)[1], [range]);
  const to = useMemo(() => ChartFormatters.TsRange(range)[0], [range]);
  const [series, setSeries] = useState<ApexAxisChartSeries>([]);

  const formatXAxis = useCallback(
    (value: string, timestamp?: number, opts?: any) => {
      return ChartFormatters.formatXAxis(range, value, timestamp, opts);
    },
    [range]
  );

  useEffect(() => {
    api(
      bucketService.getUsageHistory({
        id: props.id,
        startTime: from,
        endTime: to,
      })
    ).then((usageHistory) => {
      const { sizeSeries } = ChartFormatters.extractSeries(usageHistory);
      const updatedSeries: ApexAxisChartSeries = [];
      if (sizeSeries.length > 0) {
        updatedSeries.push({ name: "Size", data: sizeSeries });
        setRequestFailed(false);
      }
      setSeries(updatedSeries);
    });
    // eslint-disable-next-line
  }, [props.id, to, from]);

  const options = useMemo<ApexCharts.ApexOptions>(
    () => ({
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          formatter: formatXAxis,
          hideOverlappingLabels: true,
        },
      },
      chart: {
        type: "area",
        stacked: false,
        zoom: {
          type: "x",
          enabled: true,
          autoScaleYaxis: true,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      title: {
        text: "Storage Utilization",
        align: "center",
      },
      fill: {
        type: "gradient",
        colors: ["#289f04"],
        gradient: {
          shadeIntensity: 1,
          inverseColors: false,
          opacityFrom: 0.65,
          opacityTo: 0,
          stops: [0, 90, 100],
        },
      },
      yaxis: {
        labels: {
          formatter: function (val) {
            return ChartFormatters.convert(val, 0, true);
          },
        },
      },
      tooltip: {
        shared: false,
        y: {
          formatter: function (val) {
            return ChartFormatters.convert(val, 4);
          },
        },
      },
    }),
    [formatXAxis]
  );

  return (
    <>
      <ToggleButtonGroup
        className={styles.timeRangeSelection}
        color="primary"
        size="small"
        value={range}
        disabled={isLoading || requestFailed}
        exclusive
        onChange={(event, newRange) => {
          setRange(newRange);
        }}
      >
        <ToggleButton value="1D">Last 24 Hours</ToggleButton>
        <ToggleButton value="1W">Last week</ToggleButton>
        <ToggleButton value="1M">Last Month</ToggleButton>
        <ToggleButton value="6M">Last 6 Months</ToggleButton>
      </ToggleButtonGroup>
      <Chart options={options} series={series} type="area" width="100%" />
    </>
  );
}

export default UsageChart;
